import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
import { api } from '@/lib/api';
import { documentDisplayTitle } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import {
  autoPushOutcomeOf,
  rowPushReport,
  type XeroPushReport,
  type XeroRowPushReport,
} from '@/lib/xero';
import type { Quote } from '@/lib/types';

/**
 * A QUOTE NUMBER ON A LIST SURFACE — a real link that OPENS THE EDIT FORM.
 *
 * Owner's instruction, 2026-09-17: clicking a number in the tracker's
 * `Invoice/Quote Number` column should open that document's edit form, and
 * **both kinds do it** — an invoice opens the invoice form, a quote opens this
 * one. This file is the quote half.
 *
 * ── 🔴 IT IS A DELIBERATE MIRROR OF `InvoiceNumberLink`, NOT A NEW SHAPE ────
 *
 * `src/components/invoices/InvoiceNumberLink.tsx` is the specification: the
 * link/host split, the early-return rules, the `'unknown'` line count and the
 * Xero reporting are all copied from it rather than re-derived, so the two
 * numbers stacked in one tracker cell cannot behave, word or gate themselves
 * differently. **Read that file before changing this one**, and where the two
 * must diverge — and there are three places, each flagged below — the divergence
 * is a property of quotes rather than a preference.
 *
 * ⛔ **NOT merged into that file, and it must not be.** One component taking a
 * `kind` would have to hold both form types, both `api` reads and both sets of
 * Xero copy, and would then be the single place a change to either document's
 * form could break the other. Two files, one shape.
 *
 * ── 🔴 IT IS STILL A LINK, AND THAT IS THE HALF MOST EASILY LOST ───────────
 *
 * The `href` is a real `/quotes/{id}`, so ctrl/cmd-click, middle-click, "open in
 * new tab" and "copy link address" all still work and still land on the detail
 * page. {@link QuoteNumberLink} intercepts ONLY the plain primary click, and it
 * returns early — doing nothing, so the browser and the router handle it — on a
 * modified click, a non-primary button, or an event something else has already
 * claimed. **A `<button>` styled as a link would have taken all of that away
 * silently**, which is the one regression nobody reports.
 *
 * ── ⚠️ ONE MODAL PER SURFACE, NOT ONE PER ROW ──────────────────────────────
 *
 * The state and the form live in {@link QuoteQuickEdit}, which a surface mounts
 * ONCE. `QuoteFormModal` is the largest form in this app — a line grid, the
 * pickers behind it, tax rates, a letterhead read — so one per visible quote
 * number would be dozens of mounted forms on a tracker page, each with its own
 * lookups to populate. The link holds no state of its own and reaches the host
 * through context.
 *
 * ⚠️ **A link with NO host above it navigates, and does not throw.** The default
 * context offers no opener, which is exactly what a reader who may not edit gets
 * too, so there is one code path for "this click is an ordinary navigation".
 *
 * ── 🔴 THE ROWS CARRY AN ID, NOT A QUOTE ───────────────────────────────────
 *
 * `QuoteFormModal` takes a whole {@link Quote}, and a tracker row holds only
 * `{id, quoteNumber}` — so a click is a FETCH before it is a modal. That is why
 * the link has a busy state and the host has a failure state: a click that
 * silently does nothing is indistinguishable from a dead control.
 */

/* -------------------------------------------------------------------------
 * The context
 * --------------------------------------------------------------------- */

interface QuoteQuickEditContextValue {
  /**
   * `null` means **navigate instead** — no host is mounted, or this reader may
   * not update quotes.
   *
   * ⚠️ **Two different facts collapsed into one deliberately.** Both mean the
   * link must behave as an ordinary link, and keeping them apart would tempt a
   * call site into rendering a disabled control or a refusal beside a number
   * that is perfectly readable on its own page.
   */
  open: ((quoteId: number, quoteNumber: string) => void) | null;
  /** The quote currently being fetched, so the row that asked can say so. */
  pendingId: number | null;
}

const QuoteQuickEditContext = createContext<QuoteQuickEditContextValue>({
  open: null,
  pendingId: null,
});

/* -------------------------------------------------------------------------
 * The link
 * --------------------------------------------------------------------- */

export interface QuoteNumberLinkProps {
  quoteId: number;
  /**
   * What the row has to show. Rendered verbatim.
   *
   * ⚠️ **It is NOT derived from `quoteId`.** `quoteNumber` is server-allocated
   * from a ledger and is `null` until the server assigns one, so a caller
   * legitimately has an id and no number — and what to print instead is the
   * caller's decision, taken where the shape it was given is visible. The
   * tracker prints `Quote #{id}`. This component renders whatever it is handed.
   */
  quoteNumber: string;
  className?: string;
  /** The truncation channel, exactly as on the plain `Link` this replaces. */
  title?: string;
}

/**
 * One quote number: a link to `/quotes/{id}` that opens the edit form in place
 * on a plain click.
 */
export function QuoteNumberLink({
  quoteId,
  quoteNumber,
  className,
  title,
}: QuoteNumberLinkProps): ReactElement {
  const { open, pendingId } = useContext(QuoteQuickEditContext);
  const busy = pendingId === quoteId;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    /*
     * ⛔ **EVERY ONE OF THESE IS A RETURN, NOT A BRANCH.** Falling through
     * without calling `preventDefault` leaves the event exactly as the browser
     * and the router expect it, so a cmd-click opens a tab and a middle-click
     * opens a background one — the behaviours a link is chosen FOR.
     *
     * ⚠️ `react-router`'s own handler runs after this one and makes the same
     * checks, so on a modified click both stand down. The checks are written out
     * here anyway rather than leant on: this component's contract is that the
     * modifier is honoured, and a contract held only by another library's
     * internals is one nothing in this repo can see change.
     */
    if (open === null) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    open(quoteId, quoteNumber);
  };

  return (
    <Link
      to={`/quotes/${quoteId}`}
      className={className}
      {...(title !== undefined ? { title } : {})}
      /*
       * ⚠️ **`aria-busy` AND NOTHING ELSE WHILE THE FETCH RUNS.** The link is
       * deliberately NOT disabled: a second click re-issues the read, which is
       * the right answer on a request that has stalled, and an anchor that
       * disables itself also stops being reachable by keyboard and stops being
       * a link for the modifier clicks above.
       */
      {...(busy ? { 'aria-busy': true } : {})}
      onClick={handleClick}
    >
      {quoteNumber}
      {/*
       * ⚠️ **A VISIBLE MARK, because `aria-busy` alone leaves a sighted reader
       * with a click that appears to have done nothing** — the fetch is a whole
       * round trip and the modal cannot open until it lands. An ellipsis rather
       * than a spinner: it needs no animation to be legible, it survives
       * `motion-reduce`, and it costs the cell no height in either of the two
       * dense layouts this renders in.
       *
       * `aria-hidden`, because `aria-busy` is already saying this to assistive
       * tech and a bare ellipsis read aloud says nothing.
       */}
      {busy && (
        <span aria-hidden="true" className="ml-1 text-fg-subtle">
          …
        </span>
      )}
    </Link>
  );
}

/* -------------------------------------------------------------------------
 * The host
 * --------------------------------------------------------------------- */

/**
 * What a save's Xero half is worth SAYING on a list surface.
 *
 * 🔴 **A HEADER PATCH REACHES XERO.** `quotes.controller.update` calls
 * `autoPushAndRefresh` → `autoPushDocument` → `pushQuote`, so an edit made from
 * a tracker row can overwrite a document in the accountant's Xero. The server
 * swallows a failed push so the save still answers `200`, and `xeroPush` on the
 * envelope is the ONLY artefact anywhere that carries it — a caller that drops
 * it has changed a customer's document, told the person it worked, and left no
 * trace of Xero not having got it.
 *
 * ⛔ **`skipped` IS THE ORDINARY OUTCOME AND IS SUPPRESSED, NEVER WORDED AS A
 * FAILURE.** It is returned before Xero is called at all for a quote with no
 * line items, for a company nobody has linked to a Xero contact, and for every
 * quote that has never been pushed — which is most of them. Rendering it as a
 * problem would put a warning strip over the commonest save on the system and
 * teach people to disregard the one that matters. `updated` is suppressed for
 * the mirror reason: nothing went wrong and the figures on screen say so.
 *
 * ⚠️ **EVERY HEADING LEADS WITH THE QUOTE HAVING BEEN SAVED.** A person who
 * reads a refused push as *"my save was lost"* edits and saves again — and on a
 * quote each successful push is a fresh write to the customer's document, which
 * **Xero RENUMBERS on an update that does not name its number** (measured:
 * QU-0477 → QU-0479 → QU-0478). So a save repeated out of doubt can move the
 * reference the customer is holding. `rowPushReport`'s own headings are written
 * for a list row where nothing was just saved, so only its LINES — the SERVER's
 * own sentences — and its pointer are kept. Nothing here invents a sentence
 * about the cause.
 */
function savedPushReport(
  quote: Quote,
  report: XeroPushReport | undefined,
): XeroRowPushReport | null {
  const outcome = autoPushOutcomeOf(report);
  /*
   * `null` covers an absent report — a server predating `xeroPush`, or a
   * rollback — and a status this build has never heard of. Both are decided in
   * `autoPushOutcomeOf`, and neither is something to guess a tone for.
   */
  if (outcome === null) return null;
  if (outcome.kind === 'updated' || outcome.kind === 'skipped') return null;

  const name = documentDisplayTitle(quote.subject, quote.xeroQuoteNumber);
  const base = rowPushReport('quotes', name, outcome);

  /*
   * ⚠️ **`settled` IS NOT A FAILURE AND IS TONED BY `rowPushReport` AS `info`.**
   * Xero refuses a write to a quote that is no longer DRAFT there — accepted,
   * invoiced, declined — which is the design working, and the ordinary answer on
   * exactly the quotes a delivery tracker shows. It still has to be SAID: the
   * reader has just changed something here that Xero's copy will not follow.
   */
  if (outcome.kind === 'settled') {
    return { ...base, heading: `“${name}” was saved here. Xero’s copy was not changed.` };
  }

  return { ...base, heading: `“${name}” was saved, and Xero would not take the change.` };
}

export interface QuoteQuickEditProps {
  /**
   * Re-read the surface's own data. **Not optional.**
   *
   * ⚠️ **A SAVED QUOTE CAN CHANGE THE VERY ROWS THE READER IS LOOKING AT.** The
   * client-deliverables tracker is a QUERY OVER QUOTE AND INVOICE LINE ITEMS, so
   * a line added, removed or re-projected here adds, removes or re-bands rows;
   * a stage change moves the billing status the page exists to answer; and the
   * de-duplication rule means a row can legitimately leave the list entirely.
   * Leaving the tracker showing rows the save has just invalidated is the
   * failure this prop exists to prevent, and it is silent: nothing errors, the
   * rows are merely the ones from before.
   */
  onSaved: () => void;
  children: ReactNode;
}

/**
 * The one mounted edit form behind every {@link QuoteNumberLink} beneath it.
 *
 * ⚠️ **MOUNT IT ONCE PER SURFACE.** Two hosts on one page would each hold their
 * own open quote and their own notices, and a link would reach whichever is
 * nearer in the tree — which is not a thing a reader could be told.
 */
export function QuoteQuickEdit({ onSaved, children }: QuoteQuickEditProps): ReactElement {
  const { ability } = useAuth();

  /*
   * ⛔ **THE FORM DOES NOT GATE ITS OWN SAVE, SO THE GATE HAS TO BE HERE.**
   * Measured in `QuoteFormModal`: `saveDisabled` on an edit reads
   * `(!isDirty && lineEdits.dirty === 0) || lineEdits.blocked > 0` and consults
   * no ability at all — every existing caller gates the AFFORDANCE instead
   * (`QuotesPage` renders its Edit button behind `ability.canUpdate('quotes')`).
   * So a reader who cannot write quotes is given the ordinary navigation: the
   * detail page, which shows them everything and offers nothing that 403s.
   *
   * ⚠️ **It is the form's FOOTER Save that is ungated.** The line grid inside
   * takes `canEdit={ability.canUpdate('quotes')}` and gates itself — which is
   * exactly why the gap is easy to miss: the biggest control on the form is
   * already correct, and the one that writes the header is not.
   *
   * ⚠️ **`ability`, never `user.role` and never `/permissions/me`** — the rule
   * `src/lib/permissions.ts` states at length. The gate is the optimisation and
   * the server's 403 is the guarantee; an access token lasts days, so the role
   * can change under this page.
   */
  const canOpen = ability.canUpdate('quotes');

  const [pendingId, setPendingId] = useState<number | null>(null);
  const [failure, setFailure] = useState<{ quoteNumber: string; message: string } | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [push, setPush] = useState<XeroRowPushReport | null>(null);

  /*
   * ⚠️ **ONE CONTROLLER, ABORTED BY THE NEXT CLICK AND BY UNMOUNT.** Every `.catch`
   * and `.finally` below guards on `signal.aborted` — the house rule — so a read
   * this component has stopped caring about can neither raise a failure notice
   * nor clear a busy state that now belongs to a later click.
   */
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const open = useCallback((quoteId: number, quoteNumber: string) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setFailure(null);
    setPush(null);
    setPendingId(quoteId);

    void api
      .getQuote(quoteId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setQuote(envelope.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setFailure({
          quoteNumber,
          message: caught instanceof Error ? caught.message : 'The request did not complete.',
        });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setPendingId(null);
      });
  }, []);

  const context = useMemo<QuoteQuickEditContextValue>(
    () => ({ open: canOpen ? open : null, pendingId }),
    [canOpen, open, pendingId],
  );

  return (
    <QuoteQuickEditContext.Provider value={context}>
      {/*
       * ⚠️ **THE NOTICES SIT ABOVE THE CONTENT, not beside the row that was
       * clicked.** A saved quote reloads the surface, and a row can legitimately
       * leave it — the tracker is derived from line items and de-duplicates a
       * quote row the moment an invoice covers it — so a notice anchored to the
       * row would take the Xero report with it exactly when the report is the
       * only record that Xero has not got the change.
       */}
      {failure !== null && (
        <Notice
          tone="danger"
          role="alert"
          action={
            <Button variant="ghost" size="sm" onClick={() => setFailure(null)}>
              Dismiss
            </Button>
          }
        >
          <div className="min-w-0">
            <p className="m-0 font-medium text-fg [overflow-wrap:anywhere]">
              {/*
               * ⚠️ **IT NAMES THE QUOTE.** This surface shows dozens of numbers
               * at once — a tracker row can name a quote and several invoices in
               * one cell — and "a quote could not be opened" sends the reader
               * hunting for which.
               */}
              Quote {failure.quoteNumber} could not be opened.
            </p>
            {/* The server's own sentence, never replaced by one of ours. */}
            <p className="m-0 mt-1 [overflow-wrap:anywhere]">{failure.message}</p>
          </div>
        </Notice>
      )}

      {push !== null && (
        <div className="overflow-hidden rounded-md border border-border">
          {/*
           * Rendered by the component the list rows and both document forms
           * already use, so one server code cannot be worded three ways. See
           * {@link savedPushReport} for the only part that is ours.
           */}
          <XeroPushReportBanner report={push} onDismiss={() => setPush(null)} />
        </div>
      )}

      {children}

      {/*
       * Mounted only while a quote is open, so the form's state is fresh on
       * every open rather than relying on its remount key to notice the change.
       * That matters more here than on the invoice form: `QuoteFormModal`'s
       * `createKeyOf` is a CREATE-path key, so an edit has nothing keying it at
       * all and the unmount IS the reset.
       */}
      {quote !== null && (
        <QuoteFormModal
          open
          quote={quote}
          /*
           * ⚠️ **`'unknown'`, NEVER `false`, AND THIS IS THE ONE PROP TO GET
           * RIGHT.** `GET /quotes/{id}` carries no lines at all — the count is
           * a second call this host deliberately does not make — and `'unknown'`
           * is treated exactly as `true`, which LOCKS the four header totals.
           * Offering those inputs on a quote that has lines is the destructive
           * direction: a header PATCH writes literally what it is sent and never
           * re-derives, the next line write silently replaces it, the replaced
           * figures are kept nowhere, and `convert` copies all four onto the
           * invoice verbatim. Refusing them merely sends somebody to the detail
           * page, which knows the count and offers them there.
           *
           * ⚠️ **On THIS surface the safe answer costs nothing, and it is worth
           * knowing why**: a client-deliverables row IS a quote or invoice LINE,
           * so any quote reachable from that column provably has at least one.
           * `true` would be defensible here and behaves identically — but the
           * caller genuinely does not hold a count, and `'unknown'` is the
           * honest statement of that, which is what keeps it correct if this
           * host is ever mounted on a surface where the coincidence does not
           * hold.
           */
          hasLineItems="unknown"
          onClose={() => setQuote(null)}
          /*
           * ⚠️ **`onSaved` IS A CLOSE**, and the form calls `onClose` straight
           * after this — so the close below is belt-and-braces rather than the
           * mechanism. What matters here is the reload: see
           * {@link QuoteQuickEditProps.onSaved}.
           *
           * ⚠️ The saved `Quote` the form hands back is deliberately dropped.
           * The surface re-reads its own rows, which is the only thing that can
           * answer where this quote's lines now belong — the tracker bands and
           * pages server-side, so patching a row locally would place it outside
           * both.
           */
          onSaved={() => {
            setQuote(null);
            onSaved();
          }}
          /*
           * ⚠️ **A SECOND, NARROWER SIGNAL, and deliberately not `onSaved`.** The
           * line grid saves on its own button while this form stays open; on a
           * tracker built FROM those lines the rows behind are stale from that
           * instant — a line's project decides which band the row sits in — and
           * the person is still editing. Reload, close nothing.
           */
          onLinesSaved={onSaved}
          /*
           * ⚠️ **The report, never a push.** The PATCH has already been to Xero
           * by the time this fires — the server pushes inside the save — so
           * passing it buys the reader the truth and passing nothing buys
           * silence, never safety.
           */
          onXeroPush={(report) => setPush(savedPushReport(quote, report))}
        />
      )}
    </QuoteQuickEditContext.Provider>
  );
}
